import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import {
  AutomationCapabilityCatalog,
  SUPPORTED_AUTOMATION_CAPABILITY_SCHEMA_VERSIONS,
} from '@/types/automationCapabilities.ts';
import { get_automation_capabilities_url } from '@/utils/urls.ts';
import { StepType } from '@/types/automationFields.ts';

export const AUTOMATION_CAPABILITIES_QUERY_KEY = (stepType?: StepType) => ['automation-capabilities', stepType] as const;

export const isSupportedAutomationCapabilitySchema = (catalog: AutomationCapabilityCatalog): boolean =>
  SUPPORTED_AUTOMATION_CAPABILITY_SCHEMA_VERSIONS.includes(catalog.schema_version);

const useRetrieveAutomationCapabilities = (stepType?: StepType): UseQueryResult<AutomationCapabilityCatalog, Error> => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: AUTOMATION_CAPABILITIES_QUERY_KEY(stepType),
    queryFn: async () => {
      const catalog = (
        await apiClient.get<AutomationCapabilityCatalog>(get_automation_capabilities_url(stepType), {
          suppressGlobalErrorToast: true,
        })
      ).data;
      if (!isSupportedAutomationCapabilitySchema(catalog)) {
        throw new Error(`Unsupported automation capability schema version: ${catalog.schema_version}`);
      }
      return catalog;
    },
    staleTime: Infinity,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
};

export default useRetrieveAutomationCapabilities;
